import {
  MinusCircleOutlined,
  PlusCircleOutlined,
  UserAddOutlined,
  UserDeleteOutlined,
  UserSwitchOutlined,
} from '@ant-design/icons'
import { Button, Tooltip } from 'antd'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { useGetViewerQuery, useGroupPinUpdateMutation } from '@/generated/graphql'

import { ApplyButton } from '../group/group-apply/group-apply-button.component'
import { useIsGroupCreator } from '../group/group.hooks'
import { useFollow, useFollowAll } from './follow.hooks'

interface FollowProps {
  onMouseEnter?: React.MouseEvent
  onMouseLeave?: React.MouseEvent
  onFocus?: React.FocusEvent
  followeeUserId?: number
  followeeGroupId?: number
  isFollowing: boolean
  isPrivate?: boolean
  isPinned?: boolean
  isOnboarding?: boolean
  display: 'icon' | 'text' | 'both'
  component?: any
  isFollowingEachOther?: boolean
  ghost?: boolean
}

interface FollowAllProps {
  followeeIds: number[]
  target: 'group' | 'user' | 'department'
}

export const FollowAll: React.FC<FollowAllProps> = ({ followeeIds, target }) => {
  const { t } = useTranslation()
  const { followAll, loading } = useFollowAll({ followeeIds, target })

  return (
    <Button loading={loading} type='primary' onClick={followAll}>
      {t('follow.followAll')}
    </Button>
  )
}

export const Follow: React.FC<FollowProps> = ({
  component,
  display,
  followeeUserId,
  followeeGroupId,
  isFollowing,
  isPinned = false,
  isOnboarding = false,
  isPrivate = false,
  isFollowingEachOther = false,
  ghost = true,
  ...followProps
}) => {
  const [t] = useTranslation()

  const { data: userMe } = useGetViewerQuery()
  const { isGroupCreator } = useIsGroupCreator(followeeGroupId)
  const { follow, unfollow, loading } = useFollow({
    followeeUserId,
    followeeGroupId,
    isOnboarding,
  })
  const [groupPinUpdate] = useGroupPinUpdateMutation()

  if (!userMe || userMe.viewer.userId === followeeUserId) {
    return null
  }

  const onFollow = () => {
    if (isFollowing) {
      unfollow()
      if (isPinned && followeeGroupId) {
        groupPinUpdate({
          variables: {
            groupId: followeeGroupId,
          },
        })
      }
    } else {
      follow()
    }
  }

  const { text, icon, disabledDisplay } = (() => {
    const Icon = followeeGroupId
      ? isFollowing
        ? MinusCircleOutlined
        : PlusCircleOutlined
      : isFollowingEachOther
        ? UserSwitchOutlined
        : isFollowing
          ? UserDeleteOutlined
          : UserAddOutlined

    const text = isFollowingEachOther
      ? t('follow.followEachOther')
      : isFollowing
        ? t('follow.unfollow')
        : t('follow.follow')

    const icon = (
      <Icon
        style={{
          color: ghost ? (isFollowing ? '#bb4848' : '#48bbb5') : undefined,
          cursor: 'pointer',
        }}
      />
    )

    switch (display) {
      case 'both':
        return {
          icon,
          text,
          disabledDisplay: (
            <Button icon={<Icon />} style={{ color: 'grey', borderColor: 'grey' }} ghost disabled>
              {text}
            </Button>
          ),
        }

      case 'icon':
        return {
          icon,
          text: null,
          disabledDisplay: <Icon style={{ color: 'grey' }} />,
        }

      case 'text':
        return {
          icon: null,
          text,
          disabledDisplay: text,
        }
    }
  })()

  if (isGroupCreator) {
    return <Tooltip title={t('follow.followDisabledMes')}>{disabledDisplay}</Tooltip>
  }

  const followComponent = component || (
    <Button danger={isFollowing} ghost={ghost} type='primary' icon={icon} loading={loading} />
  )

  const style = ghost
    ? {
        color: isFollowing ? '#bb4848' : '#48bbb5',
        borderColor: isFollowing ? '#bb4848' : '#48bbb5',
      }
    : undefined

  const Component = React.cloneElement(
    followComponent,
    {
      ...followProps,
      onClick: onFollow,
      style,
    },
    component ? [icon, text] : text
  )

  return isPrivate && !isFollowing ? <ApplyButton groupId={followeeGroupId!} display={display} /> : Component
}
